import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { UPDATE_ACCOUNT } from '../../graphql';
import { UpdateAccountInput } from '../../types/graphql';
import { useProduct } from '../../context/ProductContext';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';

export const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (ET) - America/New_York' },
  { value: 'America/Chicago', label: 'Central Time (CT) - America/Chicago' },
  { value: 'America/Denver', label: 'Mountain Time (MT) - America/Denver' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - America/Los_Angeles' },
  { value: 'America/Phoenix', label: 'Mountain Time (MST) - America/Phoenix' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT) - America/Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST) - Pacific/Honolulu' },
  { value: 'America/Toronto', label: 'Eastern Time - America/Toronto' },
  { value: 'America/Vancouver', label: 'Pacific Time - America/Vancouver' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT) - Europe/London' },
  { value: 'Europe/Paris', label: 'Central European Time (CET) - Europe/Paris' },
  { value: 'Europe/Berlin', label: 'Central European Time - Europe/Berlin' },
  { value: 'Europe/Rome', label: 'Central European Time - Europe/Rome' },
  { value: 'Europe/Madrid', label: 'Central European Time - Europe/Madrid' },
  { value: 'Europe/Amsterdam', label: 'Central European Time - Europe/Amsterdam' },
  { value: 'Europe/Istanbul', label: 'Turkey Time (TRT) - Europe/Istanbul' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST) - Asia/Tokyo' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST) - Asia/Shanghai' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time (HKT) - Asia/Hong_Kong' },
  { value: 'Asia/Singapore', label: 'Singapore Time (SGT) - Asia/Singapore' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST) - Asia/Dubai' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST) - Asia/Kolkata' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET) - Australia/Sydney' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern Time - Australia/Melbourne' },
  { value: 'Australia/Brisbane', label: 'Australian Eastern Time - Australia/Brisbane' },
  { value: 'Australia/Perth', label: 'Australian Western Time (AWST) - Australia/Perth' },
  { value: 'America/Sao_Paulo', label: 'Brasília Time (BRT) - America/Sao_Paulo' },
  { value: 'America/Mexico_City', label: 'Central Time - America/Mexico_City' },
  { value: 'America/Buenos_Aires', label: 'Argentina Time (ART) - America/Buenos_Aires' },
];

export function DefaultsSettings() {
  // URL shortening, first-comment prefix and UTM campaign govern social
  // publishing — TinySocial only. Timezone is shared by both products.
  const { product } = useProduct();
  const isTinySocial = product === 'reach';

  const [urlShorteningEnabled, setUrlShorteningEnabled] = useState<boolean>(true);
  const [bitlyToken, setBitlyToken] = useState<string>('');
  const [firstCommentPrefix, setFirstCommentPrefix] = useState<string>('');
  const [utmCampaign, setUtmCampaign] = useState<string>('');
  const [timezone, setTimezone] = useState<string>('');

  const { selectedAccount: account, isLoading: loading, error, refetchAccounts } = useAccount();

  const [updateAccount, { loading: updating }] = useMutation(UPDATE_ACCOUNT, {
    onCompleted: () => {
      refetchAccounts();
      // TODO: Show success message
    },
    onError: (error) => {
      console.error('Error updating defaults:', error);
      // TODO: Show error message
    },
  });

  // Initialize form with the selected account's preferences
  useEffect(() => {
    if (account) {
      setUrlShorteningEnabled(account.urlShortening === true);
      setBitlyToken(account.bitlyToken || '');
      setFirstCommentPrefix(account.firstCommentPrefix || '');
      setUtmCampaign(account.utmCampaign || '');
      setTimezone(account.timezone || '');
    }
  }, [account]);

  const handleSave = async () => {
    if (!account) {
      console.error('No account found');
      return;
    }

    try {
      // Only send the publishing fields from the product that owns them, so
      // saving preferences in Atomizer can never overwrite TinySocial settings.
      const updateInput: UpdateAccountInput = {
        id: account.id,
        timezone: timezone || '',
        ...(isTinySocial
          ? {
              urlShortening: urlShorteningEnabled,
              bitlyToken: bitlyToken.trim(),
              firstCommentPrefix: firstCommentPrefix.trim(),
              utmCampaign: utmCampaign.trim(),
            }
          : {}),
      };

      await updateAccount({
        variables: {
          id: account.id,
          input: updateInput,
        },
      });
    } catch (error) {
      console.error('Error saving defaults:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 mb-6">
          <h2 className="mb-1">Defaults & Preferences</h2>
          <p className="text-muted-foreground">Configure default behaviors for common actions.</p>
        </div>
        <Card className="p-6">
          <Loader label="Loading" className="text-cmp-ink" />
        </Card>
      </div>
    );
  }

  if (error) {
    console.error(error);
    return (
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 mb-6">
          <h2 className="mb-1">Defaults & Preferences</h2>
          <p className="text-muted-foreground">Configure default behaviors for common actions.</p>
        </div>
        <Card className="p-6">
          <p className="text-error">Error loading defaults</p>
        </Card>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 mb-6">
          <h2 className="mb-1">Defaults & Preferences</h2>
          <p className="text-muted-foreground">Configure default behaviors for common actions.</p>
        </div>
        <Card className="p-6">
          <p className="text-cmp-ink">No account found</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 mb-6">
        <h2 className="mb-1">Defaults & Preferences</h2>
        <p className="text-muted-foreground">Configure default behaviors for common actions.</p>
      </div>

      <Card className="p-6 bg-core-dark border-0">
        <div className="space-y-6">
          {/* ── TinySocial-only: social publishing behaviour ─────────────── */}
          {isTinySocial && (
            <>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="url-shortening">URL Shortening</Label>
                  <p className="descriptor-text">Automatically shorten URLs when sharing content</p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-medium ${
                      urlShorteningEnabled ? 'text-success' : 'text-cmp-ink-3'
                    }`}
                  >
                    {urlShorteningEnabled ? 'ON' : 'OFF'}
                  </span>
                  <Switch
                    id="url-shortening"
                    checked={urlShorteningEnabled}
                    onCheckedChange={setUrlShorteningEnabled}
                    className={!urlShorteningEnabled ? 'border border-cmp-line-2' : ''}
                  />
                </div>
              </div>

              {urlShorteningEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="bitly-token">Bit.ly OAuth Access Token (Optional)</Label>
                  <Input
                    id="bitly-token"
                    type="password"
                    placeholder="Enter your Bit.ly OAuth access token"
                    value={bitlyToken}
                    onChange={(e) => setBitlyToken(e.target.value)}
                    className="max-w-xs"
                  />
                  <p className="descriptor-text">
                    Provide your own Bit.ly token, or leave blank to use system default shortening
                    service
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="comment-prefix">First Comment Prefix</Label>
                <Input
                  id="comment-prefix"
                  placeholder="e.g., Read More: "
                  value={firstCommentPrefix}
                  onChange={(e) => setFirstCommentPrefix(e.target.value)}
                  className="max-w-xs"
                />
                <p className="descriptor-text">Prefix added to the first comment automatically</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="utm-campaign">UTM Campaign</Label>
                <Input
                  id="utm-campaign"
                  placeholder="e.g., tinysocial"
                  value={utmCampaign}
                  onChange={(e) => setUtmCampaign(e.target.value)}
                  className="max-w-xs"
                />
                <p className="descriptor-text">Default UTM campaign parameter for tracking</p>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger id="timezone" className="max-w-xs">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px] overflow-y-auto">
                {TIMEZONE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="descriptor-text">Default timezone (PostgreSQL compatible IANA format)</p>
          </div>

          {/*
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-archive">Auto-archive Completed Items</Label>
              <p className="text-muted-foreground">
                Automatically archive items after 30 days of completion
              </p>
            </div>
            <Switch id="auto-archive" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="timezone">Default Timezone</Label>
            <Select defaultValue="america-los-angeles">
              <SelectTrigger id="timezone" className="max-w-xs">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="america-new-york">Eastern Time (ET)</SelectItem>
                <SelectItem value="america-chicago">Central Time (CT)</SelectItem>
                <SelectItem value="america-denver">Mountain Time (MT)</SelectItem>
                <SelectItem value="america-los-angeles">Pacific Time (PT)</SelectItem>
                <SelectItem value="utc">UTC</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email-notifications">Email Notifications</Label>
              <p className="text-muted-foreground">
                Receive email notifications for important updates
              </p>
            </div>
            <Switch id="email-notifications" defaultChecked />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notification-frequency">Notification Digest Frequency</Label>
            <Select defaultValue="daily">
              <SelectTrigger id="notification-frequency" className="max-w-xs">
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="realtime">Real-time</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          */}
          <div className="pt-4 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={updating}
              className="bg-button-primary text-button-primary"
            >
              {updating ? 'Saving...' : 'Save Preferences'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
